import { useSearchParams } from 'react-router-dom'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SECTOR_BUCKETS } from '@intelhub/shared/sectors'
import { VULN_CLASS_BUCKETS } from '@intelhub/shared/vuln-classes'
import { COMPARE_DIMENSIONS } from '@intelhub/shared/compare-dimensions'
import { displayMapper } from '@/lib/display-mapper'

/**
 * KAN-237 (P3+): generalised Compare picker. Replaces the sector-only
 * `SectorPicker` with a two-select picker:
 *   1. a DIMENSION select (Sector / Jurisdiction / Country / Vulnerability Class)
 *   2. a VALUE select whose options depend on the chosen dimension.
 *
 * The legacy `?sector=` URL param and `intelhub.compare.sector` localStorage key
 * remain back-compatible (see {@link useCompareSelection}).
 */

/** New dimension-aware localStorage keys (P3). */
export const COMPARE_DIMENSION_STORAGE_KEY = 'intelhub.compare.dimension'
export const COMPARE_VALUE_STORAGE_KEY = 'intelhub.compare.value'
/** Legacy sector-only key (P1/P2) — read as a migration fallback. */
export const LEGACY_SECTOR_STORAGE_KEY = 'intelhub.compare.sector'

/** Sentinel "no filter" value, shared across dimensions (mirrors sector's "all"). */
export const ALL_VALUE = 'all'

/** Human-readable labels for each dimension value. */
const DIMENSION_LABELS: Record<string, string> = {
  sector: 'Sector',
  jurisdiction: 'Jurisdiction',
  country: 'Country',
  'vuln-class': 'Vulnerability Class',
}

function dimensionLabel(dimension: string): string {
  return DIMENSION_LABELS[dimension] ?? dimension
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // ignore quota / disabled storage errors
  }
}

export interface CompareSelectionState {
  dimension: string
  value: string
  setDimension: (d: string) => void
  setValue: (v: string) => void
}

/**
 * Read/write the Compare selection (`?dimension=&value=`) from the URL, with:
 *  - back-compat for a bare `?sector=` (maps to dimension=sector&value=X), and
 *  - a localStorage migration from the legacy `intelhub.compare.sector` key to
 *    the new `intelhub.compare.dimension`/`intelhub.compare.value` keys.
 */
export function useCompareSelection(): CompareSelectionState {
  const [searchParams, setSearchParams] = useSearchParams()

  const dimension = (() => {
    const fromUrl = searchParams.get('dimension')
    if (fromUrl && COMPARE_DIMENSIONS.includes(fromUrl)) return fromUrl
    // A bare ?sector= implies the sector dimension.
    if (!fromUrl && searchParams.get('sector')) return 'sector'
    const fromStore = safeGet(COMPARE_DIMENSION_STORAGE_KEY)
    if (fromStore && COMPARE_DIMENSIONS.includes(fromStore)) return fromStore
    // Legacy key only ever held a sector value, so it implies the sector dimension.
    if (safeGet(LEGACY_SECTOR_STORAGE_KEY)) return 'sector'
    return 'sector'
  })()

  const value = (() => {
    const fromValue = searchParams.get('value')
    if (fromValue) return fromValue
    // Back-compat: a bare ?sector= supplies the value for the sector dimension.
    if (dimension === 'sector') {
      const fromSector = searchParams.get('sector')
      if (fromSector) return fromSector
    }
    const fromStore = safeGet(COMPARE_VALUE_STORAGE_KEY)
    // Only honour the stored value if the stored dimension matches the resolved
    // one (else a sector value could leak into another dimension).
    const storedDim = safeGet(COMPARE_DIMENSION_STORAGE_KEY)
    if (fromStore && storedDim === dimension) return fromStore
    // Legacy migration: the old key is a sector value.
    if (dimension === 'sector') {
      const legacy = safeGet(LEGACY_SECTOR_STORAGE_KEY)
      if (legacy) return legacy
    }
    return ALL_VALUE
  })()

  const writeParams = (nextDimension: string, nextValue: string) => {
    const next = new URLSearchParams(searchParams)
    next.set('dimension', nextDimension)
    next.set('value', nextValue)
    // Drop the legacy alias so the two params are the single source of truth.
    next.delete('sector')
    setSearchParams(next, { replace: true })
    safeSet(COMPARE_DIMENSION_STORAGE_KEY, nextDimension)
    safeSet(COMPARE_VALUE_STORAGE_KEY, nextValue)
  }

  const setDimension = (d: string) => {
    // Reset the value to "all" when the dimension changes.
    writeParams(d, ALL_VALUE)
  }

  const setValue = (v: string) => {
    writeParams(dimension, v)
  }

  return { dimension, value, setDimension, setValue }
}

export interface DimensionPickerProps {
  dimension: string
  value: string
  onDimensionChange: (d: string) => void
  onValueChange: (v: string) => void
  /**
   * Canonical jurisdiction labels present in the data (from the
   * jurisdiction-incidents response buckets). Drives the value select options
   * when dimension === 'jurisdiction'. The "no jurisdiction tagged" bucket is
   * excluded by the caller (it isn't a selectable filter).
   */
  jurisdictionOptions?: string[]
  /**
   * ISO α2 country codes present in the data (from the country-incidents
   * response buckets). Drives the value select options when
   * dimension === 'country'. The "unknown country" bucket is excluded by the
   * caller (it isn't a selectable filter). KAN-237 P4.
   */
  countryOptions?: string[]
}

export function DimensionPicker({
  dimension,
  value,
  onDimensionChange,
  onValueChange,
  jurisdictionOptions = [],
  countryOptions = [],
}: DimensionPickerProps) {
  const valueOptions: Array<{ value: string; label: string }> =
    dimension === 'jurisdiction'
      ? [
          { value: ALL_VALUE, label: 'All jurisdictions' },
          ...jurisdictionOptions.map((label) => ({ value: label, label })),
        ]
      : dimension === 'country'
        ? [
            { value: ALL_VALUE, label: 'All countries' },
            ...countryOptions.map((iso2) => ({
              value: iso2,
              label: displayMapper.country(iso2),
            })),
          ]
        : dimension === 'vuln-class'
          ? [
              { value: ALL_VALUE, label: 'All classes' },
              // Static taxonomy (not data-driven) — every class is always offered.
              ...VULN_CLASS_BUCKETS.map((b) => ({ value: b.value, label: b.label })),
            ]
          : SECTOR_BUCKETS.map((b) => ({ value: b.value, label: b.label }))

  const valueAriaLabel =
    dimension === 'jurisdiction'
      ? 'Filter by jurisdiction'
      : dimension === 'country'
        ? 'Filter by country'
        : dimension === 'vuln-class'
          ? 'Filter by vulnerability class'
          : 'Filter by sector'

  return (
    <div
      className="flex items-center gap-2"
      role="group"
      aria-label="Compare picker"
    >
      <span className="text-sm text-muted-foreground">Compare:</span>
      <Select value={dimension} onValueChange={onDimensionChange}>
        <SelectTrigger
          className="w-[160px]"
          aria-label="Compare dimension"
          data-testid="compare-dimension-select"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {COMPARE_DIMENSIONS.map((d) => (
            <SelectItem key={d} value={d}>
              {dimensionLabel(d)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          className="w-[220px]"
          aria-label={valueAriaLabel}
          data-testid="compare-value-select"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {valueOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
